import sum from 'lodash/sum';

import {
  ACTIVITY_LOG_ACTION_TYPE,
  ACTIVITY_LOG_ROLE,
  ALL_MEMBERS_SUBCHANNEL,
  CUSTOM_CHANNEL,
  FAMILY_CIRCLE_SUBCHANNEL,
  NORMAL_TYPE,
} from '../constants';

const {
  PROXY,
  PARENT,
  PATIENT,
  GUARDIAN,
  PROVIDER,
  CAREGIVER,
  GUARDIAN_PROXY,
} = ACTIVITY_LOG_ROLE;
const {
  DOWNLOAD,
  GET_ACCESS,
  SHARE_CHAT,
  SHARE_EMAIL,
  VIEW,
} = ACTIVITY_LOG_ACTION_TYPE;

export function safePhoneNumber(phone) {
  if (!phone) return '';
  return phone.replace(/[^0-9+ ]/g, '');
}

export function getSafeAbnormalStatus(value) {
  if (!value) {
    return '';
  }

  if (!NORMAL_TYPE.includes(value)) {
    return `(${value})`;
  }
}

export function getSafeNormalRange(value, units) {
  if (!value) {
    return '';
  }

  if (value) {
    if (/[0-9]/.test(value)) {
      return `${value.replace(/[()]/g, '').replace(/[-]/g, ' - ')} ${units}`;
    }
    return value.replace(/[()]/g, '');
  }
}

export function getStartEndNormalRange(value) {
  if (value && /[-]/.test(value)) {
    const rawRange = value.replace(/[()]/g, '');
    const indexOfDivider = rawRange.indexOf('-');
    const startPointNormal = parseFloat(rawRange.substring(0, indexOfDivider));

    const endPointNormal = parseFloat(
      rawRange.substring(indexOfDivider + 1, rawRange.length),
    );

    const difference =
      parseFloat(endPointNormal, 10) - parseFloat(startPointNormal, 10);

    const startPointLow = parseFloat(startPointNormal) - difference;
    const endPointHigh = parseFloat(endPointNormal, 10) + difference;
    return {
      startPointNormal,
      endPointNormal,
      startPointLow,
      endPointHigh,
    };
  }

  return {
    startPointNormal: '',
    endPointNormal: '',
    startPointLow: '',
    endPointHigh: '',
  };
}

export function checkActiveRange(
  startPointNormal,
  endPointNormal,
  startPointLow,
  endPointHigh,
  value,
) {
  const safeValue = value ? parseFloat(value.replace(/[^\d.]/g, '')) : '';
  if (safeValue < startPointNormal) {
    return { lowActive: true };
  }

  if (safeValue >= startPointNormal && safeValue <= endPointNormal) {
    return { normalActive: true };
  }

  if (safeValue > endPointNormal) {
    return { highActive: true };
  }
  return {};
}

export function normalizeData(value, rangesPoints, sumWidth, calculatedRanges) {
  if (value <= rangesPoints[0]) {
    const pointValue = calculatedRanges[0];
    const pointLeftPositionPercentage = (pointValue / sumWidth) * 100 + 3;
    return {
      pointValue,
      pointLeftPositionPercentage,
    };
  }

  if (value === parseFloat(rangesPoints[2])) {
    const pointValue =
      calculatedRanges[0] + calculatedRanges[1] + calculatedRanges[2];
    const pointLeftPositionPercentage = (pointValue / sumWidth) * 100 - 0.5;
    return {
      pointValue,
      pointLeftPositionPercentage,
    };
  }

  if (value >= rangesPoints[3]) {
    const pointValue = sum(calculatedRanges);
    const pointLeftPositionPercentage = (pointValue / sumWidth) * 100 - 3;
    return {
      pointValue,
      pointLeftPositionPercentage,
    };
  }

  const pointValue = value - rangesPoints[0];
  const pointLeftPositionPercentage = (pointValue / sumWidth) * 100;
  return {
    pointValue,
    pointLeftPositionPercentage,
  };
}

export function downloadFile(url, fileType, title, mimeType, handleError) {
  // eslint-disable-next-line compat/compat
  fetch(url, {
    method: 'GET',
  })
    .then(resp => {
      return resp.blob();
    })
    .then(blob => {
      const newBlob = new Blob([blob], {
        type: mimeType,
        charset: 'UTF-8',
      });
      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob
      // eslint-disable-next-line promise/always-return
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(newBlob);
        return;
      }
      const data = window.URL.createObjectURL(newBlob);
      const link = document.createElement('a');
      link.dataType = 'json';
      link.href = data;
      link.download = `${title}.${fileType}`;
      link.dispatchEvent(new MouseEvent('click'));
      setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
      }, 60);
    })
    .catch(handleError);
}

export const getSafeChannelName = (name, formatMessage) => {
  switch (name) {
    case CUSTOM_CHANNEL:
      return formatMessage({ id: 'chat.titles.customChannel' });
    case ALL_MEMBERS_SUBCHANNEL:
      return formatMessage({ id: 'chat.titles.generalChatTab' });
    case FAMILY_CIRCLE_SUBCHANNEL:
      return formatMessage({ id: 'chat.titles.familyChatTab' });
    default:
      return name || '';
  }
};

export const getSafeUserId = (targetUserId, mainUserId) => {
  const isMainUser =
    targetUserId === mainUserId || (mainUserId && !targetUserId);
  return { safeUserId: isMainUser ? mainUserId : targetUserId, isMainUser };
};

export const getSafeRole = (role, formatMessage) => {
  switch (role) {
    case CAREGIVER: {
      return formatMessage({
        id: 'circles.roles.caregiver',
      });
    }
    case GUARDIAN_PROXY: {
      return formatMessage({
        id: 'patient.portal.healthRecord.activityLog.guardianProxy',
      });
    }
    case PROVIDER: {
      return formatMessage({
        id: 'messages.nav.tabs.provider',
      });
    }
    case PATIENT: {
      return formatMessage({
        id: 'patient.portal.healthRecord.activityLog.patient',
      });
    }
    case GUARDIAN: {
      return formatMessage({
        id: 'dependent.form.guardianTitle',
      });
    }
    case PROXY: {
      return formatMessage({
        id: 'dependent.form.proxyTitle',
      });
    }
    case PARENT: {
      return formatMessage({
        id: 'dependent.form.parentTitle',
      });
    }
    default:
      return '';
  }
};

export const getSafeAction = (
  action,
  type,
  title,
  fileName,
  chatRoomName,
  emailAddress,
  participantInChat,
  formatMessage,
) => {
  const transmitted = formatMessage({
    id: 'patient.portal.healthRecord.activityLog.transmitted',
  });
  const to = formatMessage({
    id: 'patient.portal.healthRecord.activityLog.to',
  });
  const downloaded = formatMessage({
    id: 'patient.portal.healthRecord.activityLog.downloaded',
  });
  const viewed = formatMessage({
    id: 'patient.portal.healthRecord.activityLog.viewed',
  });
  switch (action) {
    case SHARE_CHAT: {
      const safeChannelName = getSafeChannelName(chatRoomName, formatMessage);
      return `${transmitted} ${fileName} ${to} ${safeChannelName}`;
    }
    case SHARE_EMAIL: {
      return `${transmitted} ${fileName} ${to} ${emailAddress}`;
    }
    case DOWNLOAD: {
      return `${downloaded} ${fileName}`;
    }
    case GET_ACCESS: {
      return `${viewed} ${title || type}`;
    }
    case VIEW: {
      return `${viewed} ${fileName}`;
    }
    default:
      return '';
  }
};

export const parseFileAttribute = data => {
  const isPdfFormat = data.some(file => file.extension === 'pdf');
  const isXmlFormat = data.some(file => file.extension === 'xml');
  const isTxtFormat = data.some(file => file.extension === 'txt');
  const isZipFormat = data.some(file => file.extension === 'zip');
  const xmlFileData = isXmlFormat
    ? data.find(file => file.extension === 'xml')
    : {};
  const pdfFileData = isPdfFormat
    ? data.find(file => file.extension === 'pdf')
    : {};
  const txtFileData = isTxtFormat
    ? data.find(file => file.extension === 'txt')
    : {};
  const zipFileData = isZipFormat
    ? data.find(file => file.extension === 'zip')
    : {};
  const anyTypeFileData = data[0];

  return {
    zipFileData,
    isXmlFormat,
    isTxtFormat,
    isPdfFormat,
    xmlFileData,
    pdfFileData,
    txtFileData,
    anyTypeFileData,
  };
};



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/utils.js